import z from 'zod';
import { partySchema } from '../../common';
import { dateSchema } from '../../validation';

/**
 * What kind of interaction the action represents.
 *
 * - `note` is the free-form sales log the admin Logs tab writes —
 *   a Monday-style update with no type / status / outcome form.
 * - `phone_call` / `email` / `in_person_visit` / `online_meeting` are
 *   things the sales team did TO the lead. Historic rows still use
 *   these; new updates from the composer are `note`.
 * - `form_submitted` is a system-stamped action captured when the
 *   lead comes in through a public form (landing "Book a demo",
 *   inbound contact form, etc.). It represents what the LEAD did,
 *   not what we did, and cannot be forged from the admin UI.
 * - `whatsapp_conversation` is system-stamped by the AI agent when a
 *   prospect with an OPEN lead talks to Hero AI on WhatsApp again —
 *   the agent appends this action instead of creating a duplicate
 *   lead. Also excluded from the manual set.
 */
export const leadActionTypeSchema = z.enum([
  'note',
  'phone_call',
  'email',
  'in_person_visit',
  'online_meeting',
  'form_submitted',
  'whatsapp_conversation',
]);

/**
 * Subset of {@link leadActionTypeSchema} that the sales team can
 * manually log. `form_submitted` and `whatsapp_conversation` are
 * stamped only by the backend and are therefore excluded here.
 * The admin Logs composer always writes `note`.
 */
export const leadManualActionTypeSchema = z.enum([
  'note',
  'phone_call',
  'email',
  'in_person_visit',
  'online_meeting',
]);
export type LeadManualActionType = z.infer<typeof leadManualActionTypeSchema>;

export const leadActionStatusSchema = z.enum([
  'scheduled',
  'in_progress',
  'completed_successfully',
  'completed_unsuccessfully',
  'cancelled',
]);

export type LeadActionType = z.infer<typeof leadActionTypeSchema>;
export type LeadActionStatus = z.infer<typeof leadActionStatusSchema>;

export const leadActionSchema = z.object({
  /** the date of the conversation. */
  date: dateSchema,
  /** the type of the conversation. */
  type: leadActionTypeSchema,
  /** the status of the conversation. */
  status: leadActionStatusSchema,
  /** outcome of the action. */
  outcome: z.coerce.string().optional(),
  /** notes from the internal team. */
  internalNotes: z.string().optional(),
  /**
   * Who logged this action. AdminUser party snapshot, stamped by the
   * backend from the session — the client cannot set this. Omitted on
   * system-stamped rows (`form_submitted`, `whatsapp_conversation`)
   * and on historic actions written before this field existed.
   */
  by: partySchema.optional(),
});
export type LeadAction = z.infer<typeof leadActionSchema>;
